/**
 * Build the corner-fill objects at a non-right-angle (obtuse / acute)
 * joint between two cabinet walls. Returns `{ cabinet, countertop }`
 * — a polygon-shaped lower cabinet body PLUS the countertop slab on
 * top of it.
 *
 * At an obtuse / acute joint, the two regular wall cabinets stop
 * short of the corner so their inner-room corners just touch on the
 * angle bisector (`materializeCandidate.js` reserves `depth / tan(θ/2)`
 * along each wall). The remaining void in the corner used to be left
 * with only a countertop floating above empty space — real kitchens
 * fill it with a cabinet body too. Both objects share the same
 * 4-vertex polygon footprint (the "kite"); the cabinet body is full
 * counter height, the countertop is the slab on top.
 *
 * Stop distances are per-wall (`stopA` / `stopB`, defaulting to the
 * symmetric `stopDistance`). When a wall's first cabinet / appliance
 * sits farther from the corner than the touching distance but the
 * intervening gap is too small to host its own standalone cabinet,
 * the caller extends that wall's stop out to the near edge of that
 * placement so the corner unit combines with the adjacent slot — no
 * bare-counter break (see USER RULE in materializeCandidate).
 *
 * Polygon vertices, going CCW from the corner:
 *   V1  = corner point itself
 *   V2  = wall A surface point at `stopA` from corner
 *   V3a = front corner of wall A's cabinet = `V2 + depth·nA`
 *   V3b = front corner of wall B's cabinet = `V4 + depth·nB`
 *   V4  = wall B surface point at `stopB` from corner
 *
 * The cabinet fronts are each capped at the 24" counter depth, so
 * `V3a`→`V3b` is the corner-fill's own front edge (flush with the two
 * adjacent runs). For the symmetric touching case
 * (stopA === stopB === depth/tan(θ/2)) `V3a` and `V3b` coincide on the
 * angle bisector and the pentagon collapses to the original 4-vertex
 * kite — byte-identical to the pre-asymmetry output.
 */

const {
  KITCHEN_CABINET_DIMENSIONS,
  KITCHEN_COUNTERTOP_THICKNESS,
} = require('./constants');
const { getWallAxes } = require('./wallMath');

/**
 * Below this a strip is a reveal, not a panel — the counter's own overhang
 * covers it and a piece that narrow would render with its own door and
 * handle. Mirrors `MIN_FILLER_CABINET_WIDTH` in post-processing/kitchen.js,
 * which draws the same line for the run engine's own fillers.
 */
const MIN_FILLER_PANEL_M = 3 * 0.0254;

/** Two polygon vertices closer than a tenth of a millimetre are one vertex. */
const VERTEX_EPS_M = 1e-4;
const dot = (a, b) => a.x * b.x + a.z * b.z;

function pointsCoincide(a, b) {
  return (
    Math.abs(a.x - b.x) < VERTEX_EPS_M && Math.abs(a.z - b.z) < VERTEX_EPS_M
  );
}

/**
 * `center` is the BOUNDING-BOX centre, because `dimensions` are the
 * bounding-box extents and the two have to describe the same box (see the
 * note at the call site).
 */
function boundsOf(polygon) {
  const xs = polygon.map((p) => p.x);
  const zs = polygon.map((p) => p.z);
  const minX = Math.min(...xs);
  const minZ = Math.min(...zs);
  const bboxX = Math.max(...xs) - minX;
  const bboxZ = Math.max(...zs) - minZ;
  return { cx: minX + bboxX / 2, cz: minZ + bboxZ / 2, bboxX, bboxZ };
}

/**
 * The offset from the corner where two lines cross, each line given by its
 * normal and its signed offset along it: u with u·nA = offA and u·nB = offB.
 * Null for parallel lines.
 */
function linesCrossing(nA, offA, nB, offB) {
  const det = nA.x * nB.z - nA.z * nB.x;
  if (Math.abs(det) < 1e-9) return null;
  return {
    x: (offA * nB.z - offB * nA.z) / det,
    z: (offB * nA.x - offA * nB.x) / det,
  };
}

/**
 * Where the two runs' countertop FRONTS meet, and so where each run's slab
 * has to end. `frontA` / `frontB` are how far each slab reaches in front of
 * its line (cabinet depth plus the front overhang).
 *
 * The cabinets meet on one point, but their slabs overhang it, and two
 * square-ended slabs at an inside corner cross by the overhang — a sliver of
 * one lying on the other. Each slab ends instead where the front lines cross
 * (`seamA` / `seamB` along each leg, a little past the cabinet stop), and the
 * wedge's countertop, which can take any outline, fills the angled sliver
 * between.
 *
 * Null when the fronts do not cross beyond both cabinet stops (no overhang,
 * or not an inside corner): there is nothing to give the wedge.
 */
function slabSeams({ nA, dA, frontA, stopA, nB, dB, frontB, stopB }) {
  if (!(frontA > 0) || !(frontB > 0)) return null;
  const u = linesCrossing(nA, frontA, nB, frontB);
  if (!u) return null;
  const seamA = u.x * dA.x + u.z * dA.z;
  const seamB = u.x * dB.x + u.z * dB.z;
  if (seamA <= stopA + VERTEX_EPS_M || seamB <= stopB + VERTEX_EPS_M)
    return null;
  return { u, seamA, seamB };
}

/**
 * The countertop's outline: the body's, moved out to meet the runs' slabs.
 *
 * BACK (`backA` / `backB`, metres behind each leg's line, default 0): a run
 * against a wall has a slab flush with the wall. A free-standing counter's
 * slab reaches past its finished back panel (2" on FF158948), and a wedge top
 * stopping at the cabinet line left a 2" step in the counter's back edge. The
 * corner vertex moves to where the two moved back lines cross.
 *
 * FRONT (`slabFrontA` / `slabFrontB`, see `slabSeams`): the front vertex moves
 * from the cabinets' meeting point to the slabs', and each side edge to the
 * seam where that run's slab now ends.
 *
 * Returns `polygon` itself when neither applies — the same object, so a
 * corner without them stays byte-identical.
 */
function slabPolygon(polygon, corner, { point, nA, nB, dA, dB, stopA, stopB }) {
  const backA = corner.backA > VERTEX_EPS_M ? corner.backA : 0;
  const backB = corner.backB > VERTEX_EPS_M ? corner.backB : 0;
  const seams =
    corner.slabFrontA != null && corner.slabFrontB != null
      ? slabSeams({
          nA,
          dA,
          frontA: corner.slabFrontA,
          stopA,
          nB,
          dB,
          frontB: corner.slabFrontB,
          stopB,
        })
      : null;
  if (!backA && !backB && !seams) return polygon;
  const back = linesCrossing(nA, -backA, nB, -backB);
  if (!back) return polygon;
  const at = (u) => ({ x: point.x + u.x, z: point.z + u.z });
  const on = (s, d, n, off) => ({
    x: point.x + d.x * s + n.x * off,
    z: point.z + d.z * s + n.z * off,
  });
  if (seams) {
    return [
      at(back),
      on(seams.seamA, dA, nA, -backA),
      at(seams.u),
      on(seams.seamB, dB, nB, -backB),
    ];
  }
  const shift = (p, n, d) => ({ x: p.x - n.x * d, z: p.z - n.z * d });
  const last = polygon.length - 1;
  return [
    at(back),
    shift(polygon[1], nA, backA),
    ...polygon.slice(2, last),
    shift(polygon[last], nB, backB),
  ];
}

function buildCornerFillCountertop({ corner, floorY }) {
  const { wallA, wallB, wallBEnd, point } = corner;
  const stopDefault = corner.stopDistance || 0;
  const stopA = corner.stopA != null ? corner.stopA : stopDefault;
  const stopB = corner.stopB != null ? corner.stopB : stopDefault;
  if (stopA <= 0 || stopB <= 0) return null;

  const axesA = getWallAxes(wallA);
  const axesB = getWallAxes(wallB);
  if (!axesA || !axesB) return null;

  const dA = axesA.wallDir;
  const dB =
    wallBEnd === 'left'
      ? axesB.wallDir
      : { x: -axesB.wallDir.x, z: -axesB.wallDir.z };
  const nA = axesA.wallNormal;
  const nB = axesB.wallNormal;
  // Each leg's cabinets stand on the side of their wall that faces the other
  // leg. A wall B oriented the other way round (`wallBEnd` naming the end
  // that is NOT at the corner) puts its normal out of the room, and the
  // asymmetric front corner below would land a cabinet depth beyond the wall.
  if (dot(nA, dB) < -1e-6 || dot(nB, dA) < -1e-6) {
    throw new Error(
      'buildCornerFillCountertop: a leg faces away from the other — ' +
        "wallB must be passed with `wallBEnd` naming its corner end and wallA's left normal pointing at wall B"
    );
  }
  // Per-side cabinet depth (scan path: a run's real depth can be 19"–24");
  // defaults to the standard so the layout path is unchanged.
  const cabinetDepth = KITCHEN_CABINET_DIMENSIONS.depth;
  const depthA = corner.depthA ?? cabinetDepth;
  const depthB = corner.depthB ?? cabinetDepth;
  const cabinetHeight = KITCHEN_CABINET_DIMENSIONS.height;
  const thickness = KITCHEN_COUNTERTOP_THICKNESS;

  const v1 = { x: point.x, z: point.z };
  const v2 = { x: point.x + stopA * dA.x, z: point.z + stopA * dA.z };
  const v4 = { x: point.x + stopB * dB.x, z: point.z + stopB * dB.z };
  // Front corners of each adjacent run's cabinet, capped at the 24"
  // counter depth. In the symmetric touching case they coincide on the
  // angle bisector; keep the original explicit 4-vertex kite there so
  // all existing (symmetric) output stays byte-identical, and only
  // build the 5-vertex pentagon when the legs are asymmetric.
  const v3a = { x: v2.x + depthA * nA.x, z: v2.z + depthA * nA.z };
  let polygon;
  if (stopA === stopB && depthA === depthB) {
    polygon = [v1, v2, v3a, v4];
  } else {
    const v3b = {
      x: v4.x + depthB * nB.x,
      z: v4.z + depthB * nB.z,
    };
    // The asymmetric branch reaches the coincident case too: whenever the
    // legs' stop/depth pairs happen to put both front corners on the same
    // point, `v3b` duplicates `v3a`. Emitting it anyway leaves a ZERO-LENGTH
    // edge in `polygonCorners`, which SAT's `getEdges` normalises by length —
    // and it dragged the vertex mean this file used to centre on toward the
    // doubled corner. Fall back to the 4-vertex kite instead, which is the
    // same shape with one fewer vertex.
    polygon = pointsCoincide(v3a, v3b)
      ? [v1, v2, v3a, v4]
      : [v1, v2, v3a, v3b, v4];
  }
  const { cx, cz, bboxX, bboxZ } = boundsOf(polygon);
  // `center` is the BOUNDING-BOX centre, because `dimensions` below are the
  // bounding-box extents and the two have to describe the same box. It used
  // to be the mean of the polygon's vertices, which agrees with the bbox
  // centre only for a symmetric polygon — on structure 08E54F9E's two corner
  // fills the pair disagreed by 6.8cm and 5.8cm in x. `polygonCorners` are
  // ROOM coordinates, and both renderers subtract `center` from them
  // (`objectDrawingUtils`'s corner-fill case, `Countertop.jsx`'s extrude
  // path), so they were always right; every consumer reading `center` +
  // `dimensions` — collision
  // cleanup, clearance resolution, selection bounds, elevations — saw the
  // slab displaced by up to 2.7".
  const top = slabPolygon(polygon, corner, {
    point,
    nA,
    nB,
    dA,
    dB,
    stopA,
    stopB,
  });
  const topBounds = top === polygon ? null : boundsOf(top);

  const stamp = `${wallA.id}_${wallB.id}`;
  const polygonCorners = polygon.map((p) => ({ x: p.x, z: p.z }));
  const lastModified = new Date().toISOString();
  const cabinet = {
    id: `corner_fill_cabinet_${stamp}`,
    type: 'corner-fill-cabinet',
    label: 'Corner-Fill Cabinet',
    // 'lower-cabinet' so bulk restyle surfaces (web CABINET_SURFACE set +
    // the styling brain's CABINET_TYPES) see it (#1535/M10) — the panel's
    // per-object path always did.
    category: {
      cabinet: true,
      'lower-cabinet': true,
      'corner-fill-cabinet': true,
    },
    center: {
      x: cx,
      y: floorY + cabinetHeight / 2,
      z: cz,
    },
    dimensions: {
      x: bboxX,
      y: cabinetHeight,
      z: bboxZ,
    },
    rotation: { x: 0, y: 0, z: 0 },
    polygonCorners,
    parentWallIds: [wallA.id, wallB.id],
    addedBy: 'kitchen-layout',
    lastModified,
  };
  const countertop = {
    id: `corner_fill_countertop_${stamp}`,
    type: 'corner-fill-countertop',
    label: 'Corner-Fill Countertop',
    category: { countertop: true, 'corner-fill-countertop': true },
    center: {
      x: topBounds ? topBounds.cx : cx,
      y: floorY + cabinetHeight + thickness / 2,
      z: topBounds ? topBounds.cz : cz,
    },
    // Bounding-box dimensions for legacy renderers / collision code.
    // Layout-aware renderers should read `polygonCorners` instead.
    dimensions: {
      x: topBounds ? topBounds.bboxX : bboxX,
      y: thickness,
      z: topBounds ? topBounds.bboxZ : bboxZ,
    },
    rotation: { x: 0, y: 0, z: 0 },
    polygonCorners: top.map((p) => ({ x: p.x, z: p.z })),
    parentWallIds: [wallA.id, wallB.id],
    addedBy: 'kitchen-layout',
    lastModified,
  };
  return { cabinet, countertop };
}

/**
 * A RECTANGULAR filler standing on one wall, plus the counter over it —
 * the piece that closes a strip of run nothing owns.
 *
 * Sibling of `buildCornerFillCountertop`, and deliberately NOT the same
 * shape. A corner fill is a wedge whose front edge runs from one run's front
 * corner to the other's, so its face angle is set by where the two runs stop;
 * growing one leg to swallow an adjacent strip ROTATES that face off the
 * mitre the two counters meet on (user ruling 2026-09-03). A strip beside the
 * wedge is its own square-faced panel instead, which is also what a real
 * kitchen does with a gap too narrow to be a cabinet.
 *
 * Emitted as a plain box rather than a polygon piece: a rectangle is fully
 * described by centre + dimensions + rotation, and that way it renders with
 * an ordinary door face like every other filler in the run.
 *
 * `from`/`to` are distances along `dir` from `point`; `depth` is measured
 * along the wall's inward normal. A wall-mounted fixture's local +Z points
 * along that normal, which is what fixes the rotation.
 *
 * @returns {{cabinet, countertop}|null} null when the strip is too small to
 *   be a panel at all — `minWidth` is the caller's "a reveal, not a cabinet"
 *   line, so the two passes using this share one answer.
 */
function buildWallStripFiller({
  wall,
  point,
  dir,
  from,
  to,
  depth,
  floorY,
  idStamp,
  minWidth = 0,
}) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const width = Math.abs(to - from);
  if (!(width > minWidth) || !(depth > minWidth)) return null;

  const n = axes.wallNormal;
  const mid = (from + to) / 2;
  const cx = point.x + mid * dir.x + (depth / 2) * n.x;
  const cz = point.z + mid * dir.z + (depth / 2) * n.z;
  const cabinetHeight = KITCHEN_CABINET_DIMENSIONS.height;
  const thickness = KITCHEN_COUNTERTOP_THICKNESS;
  // Local +Z along the inward normal — the convention every wall-mounted
  // fixture in the scene already follows.
  const rotationY = Math.atan2(n.x, n.z);
  const lastModified = new Date().toISOString();

  const box = (y, height) => ({
    center: { x: cx, y, z: cz },
    dimensions: { x: width, y: height, z: depth },
    rotation: { x: 0, y: rotationY, z: 0 },
    parentWallIds: [wall.id],
    addedBy: 'kitchen-layout',
    lastModified,
  });

  const cabinetId = `strip_filler_cabinet_${idStamp}`;
  return {
    cabinet: {
      id: cabinetId,
      type: 'lower-cabinet',
      label: 'Cabinet Filler',
      category: { cabinet: true, 'lower-cabinet': true },
      ...box(floorY + cabinetHeight / 2, cabinetHeight),
    },
    countertop: {
      id: `strip_filler_countertop_${idStamp}`,
      type: 'countertop',
      label: 'Kitchen Countertop',
      category: { countertop: true },
      // The filler it sits on, so the slab follows that filler's run
      // (`countertopRunId`) — swept with it, deleted with it.
      parents: [{ id: cabinetId, type: 'lower-cabinet' }],
      ...box(floorY + cabinetHeight + thickness / 2, thickness),
    },
  };
}

/**
 * The filler a strip slab stands on, read from the id stamp the pair shares —
 * for a slab saved before it named its filler in `parents`. Every scan
 * processed until then carries the pair joined by nothing else. Null for any
 * other object.
 */
function stripFillerCabinetIdOf(countertop) {
  const m = /^strip_filler_countertop_(.+)$/.exec(countertop?.id || '');
  return m ? `strip_filler_cabinet_${m[1]}` : null;
}

module.exports = {
  buildCornerFillCountertop,
  slabSeams,
  linesCrossing,
  buildWallStripFiller,
  stripFillerCabinetIdOf,
  MIN_FILLER_PANEL_M,
};
